
import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { createNoPORequest } from '@/apis/application-form/specialPaymentRequestNoPO'
import { getCompanyAllList } from '@/apis/main-data/company'
import { getSupplierAllList, getSupplierById } from '@/apis/main-data/supplier'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import Container from '@/components/form/container/Container.vue'
import { ElMessage } from 'element-plus'
import MultipleColumnsFrom from '@/components/form/common/MultipleColumnsFrom.vue'
import dayjs from 'dayjs'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { CreateNoPORequestModel } from '@/model/application-form/specialPaymentRequestNoPO'
import { SupplierBankModel, SupplierInfoModel, SupplierListParamModel } from '@/model/master-data/supplierModel'
import { useRouter } from 'vue-router'
import { createPositiveNumberValidator } from '@/utils/validatorUtil'
import { intelligentClipboard } from '@/utils/clipboard'

export default defineComponent({
  components: {
    BaseDialog,
    Container,
    MultipleColumnsFrom
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const positiveNumberValidator = createPositiveNumberValidator(t)
    const struct = [
      { prop: 'vendorName', label: t('supplier.supplierName'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'specialPaymentType',
        label: t('approvalForm.specialPaymentType'),
        component: 'el-select',
        type: 'text',
        model: '',
        options: [
          { label: t('无PO'), value: '无PO' }
        ]
      },
      {
        prop: 'isForeignCurrency',
        label: t('bank.isForeignExchangeOrExternal'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'bankAccount', label: t('bank.bankAccount'), component: 'el-input', type: 'text', model: '' },
      { prop: 'amount', label: t('bank.amount'), component: 'el-input', type: 'text', model: '', decimalPlaces: 2, isPositive: true },
      { prop: 'bankCode', label: t('bank.bankNumber'), component: 'el-input', type: 'text', model: '' },
      { prop: 'currency', label: t('bank.currency'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankName', label: t('bank.bankName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'reference', label: t('approvalForm.reference'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankCity', label: t('bank.bankCity'), component: 'el-input', type: 'text', model: '' },
      { prop: 'paymentDate', label: t('order.paymentDate'), component: 'el-date-picker', type: 'text', model: '' },
      {
        prop: 'isCompanyAccount',
        label: t('order.isCompanyAccount'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      { prop: 'comment', label: t('approvalForm.comment'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'purpose',
        label: t('approvalForm.purpose'),
        component: 'el-input',
        type: 'textarea',
        model: '转账',
        rows: 5,
        maxlength: 100,
        showWordLimit: true
      },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), component: 'el-input', type: 'text', model: '', isSlot: true }
    ]
    const structRules = {
      vendorName: [{ required: true, message: t('supplier.supplierNameP'), trigger: 'change' }],
      specialPaymentType: [{ required: true, message: t('approvalForm.specialPaymentTypeP'), trigger: 'change' }],
      isForeignCurrency: [{ required: true, message: t('bank.isForeignExchangeOrExternalP'), trigger: 'change' }],
      companyCode: [{ required: true, message: t('company.companyCodeP'), trigger: 'change' }],
      bankAccount: [{ required: true, message: t('bank.bankAccountP'), trigger: 'blur' }],
      amount: [
        { required: true, message: t('bank.amountP'), trigger: 'blur' },
        { validator: positiveNumberValidator, trigger: 'blur' }
      ],
      bankCode: [{ required: true, message: t('bank.bankNumberP'), trigger: 'blur' }],
      currency: [{ required: true, message: t('bank.currencyP'), trigger: 'blur' }],
      bankName: [{ required: true, message: t('bank.bankNameP'), trigger: 'blur' }],
      paymentDate: [{ required: true, message: t('order.paymentDateP'), trigger: 'blur' }],
      bankCity: [{ required: true, message: t('bank.bankCityP'), trigger: 'blur' }],
      isCompanyAccount: [{ required: true, message: t('order.isCompanyAccountP'), trigger: 'change' }],
      purpose: [{ required: true, message: t('approvalForm.purposeP'), trigger: 'blur' }]
    }
    const dataMap = reactive({
      formStruct: struct,
      formRules: structRules,
      formModel: createModel(struct) as CreateNoPORequestModel,
      dialogVisible: false,
      selectedSupplier: null as SupplierInfoModel | null,
      selectedSupplierBank: null as SupplierBankModel | null,
      loadingSupplier: false,
      isEditing: true,
      supplierList: [] as any,
      supplierBankList: [] as any,
      async toSubmit(submitData: any) {
        const params: CreateNoPORequestModel = {
          vendorName: submitData.vendorName,
          vendorNumber: submitData.vendorNumber,
          specialPaymentType: submitData.specialPaymentType,
          companyCode: submitData.companyCode,
          amount: submitData.amount,
          reference: submitData.reference,
          currency: submitData.currency,
          isForeignCurrency: submitData.isForeignCurrency,
          paymentDate: dayjs(submitData.paymentDate).format('YYYY-MM-DD HH:mm:ss'),
          bankAccount: submitData.bankAccount,
          bankCode: submitData.bankCode,
          bankName: submitData.bankName,
          comment: submitData.comment,
          purpose: submitData.purpose,
          bankCity: submitData.bankCity,
          paymentDocuments: submitData.paymentDocuments,
          isCompanyAccount: submitData.isCompanyAccount
        }
        const res = await createNoPORequest(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
          await router.push({ name: 'SpecialPaymentRequestNoPOList', state: { refresh: true } })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      },
      async handleSelect() {
        dataMap.loadingSupplier = true
        const params: SupplierListParamModel = {
          accountNumber: null,
          isDelete: false,
          name: null
        }
        const res = await getSupplierAllList(params)
        dataMap.supplierList = Array<any>()
        if (res?.data) {
          res.data.forEach(supplier => {
            if (dataMap.supplierList) {
              dataMap.supplierList.push({
                label: supplier.accountNumber + ', ' + supplier.name,
                value: supplier
              })
            }
          })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        dataMap.loadingSupplier = false
        dataMap.dialogVisible = true
      },
      async onPurposeDialogConfirm() {
        if (dataMap.selectedSupplier) {
          dataMap.formModel.vendorName = dataMap.selectedSupplier.name
          dataMap.formModel.vendorNumber = dataMap.selectedSupplier.accountNumber
          dataMap.formModel.isForeignCurrency = dataMap.selectedSupplier.isForeignCurrency
        } else {
          dataMap.formModel.vendorName = ''
        }
        if (dataMap.selectedSupplierBank) {
          dataMap.formModel.bankName = dataMap.selectedSupplierBank.name
          dataMap.formModel.bankAccount = dataMap.selectedSupplierBank.account
          dataMap.formModel.bankCity = dataMap.selectedSupplierBank.city
          dataMap.formModel.bankCode = dataMap.selectedSupplierBank.code
          dataMap.formModel.isCompanyAccount = dataMap.selectedSupplierBank.isCompanyAccount
        } else {
          dataMap.formModel.bankName = ''
          dataMap.formModel.bankAccount = ''
          dataMap.formModel.bankCity = ''
          dataMap.formModel.bankCode = ''
        }
        dataMap.dialogVisible = false
      },
      async handleClickLink(event: MouseEvent) {
        await intelligentClipboard(dataMap.formModel.paymentDocuments, event)
      }
    })
    const getCompanyList = async() => {
      dataMap.formStruct[3].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.formStruct[3].options = optionList
      dataMap.formStruct[3].loading = false
    }
    const getSupplierBankOption = async(supplierId: number) => {
      const optionList = []
      const res = await getSupplierById(supplierId)
      const selectedSupplier = res?.data ?? null
      if (selectedSupplier && selectedSupplier.vendorBanks.length > 0) {
        for (const bank of selectedSupplier.vendorBanks) {
          optionList.push({
            label: bank.name + ', ' + bank.account,
            value: bank
          })
        }
      }
      dataMap.supplierBankList = optionList
    }
    watch(() => dataMap.selectedSupplier, (value) => {
      dataMap.supplierBankList = []
      dataMap.selectedSupplierBank = null
      if (value) {
        getSupplierBankOption(value.id)
      }
    })
    watch(dataMap.formModel, (value: CreateNoPORequestModel) => {
      if (value && dataMap.selectedSupplier) {
        if (value.vendorName !== dataMap.selectedSupplier.name) {
          dataMap.formModel.vendorNumber = null
        } else if (dataMap.selectedSupplierBank && value.bankAccount !== dataMap.selectedSupplierBank.account) {
          dataMap.formModel.vendorNumber = null
        }
      }
    })
    onMounted(() => {
      getCompanyList()
    })
    return {
      t,
      ...toRefs(dataMap)
    }
  }
})
